import apiClient from '../api/api';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * Interfaz que representa la estructura de respuesta estándar de tu API.
 * @template T - El tipo de los datos contenidos en la propiedad 'data'.
 */
interface ApiResponse<T> {
  status: number;
  data: T;
  // Puedes añadir otras propiedades que tu API devuelva, como 'message'.
}

/**
 * Realiza una petición GET para obtener recursos.
 * @template T - El tipo de dato que se espera recibir.
 * @param {string} url - El endpoint al que se hará la petición.
 * @param {AxiosRequestConfig} [config] - Configuración opcional de Axios.
 * @returns {Promise<T>} - Una promesa que resuelve a los datos extraídos de la respuesta.
 */
const get = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  // Se especifica que la respuesta de Axios tendrá la forma de ApiResponse<T>
  const response: AxiosResponse<ApiResponse<T>> = await apiClient.get(url, config);
  // Se retorna directamente la data anidada, simplificando el uso en los componentes.
  return response.data.data;
};
// En tu archivo apiService.ts

const getSecondary = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  const response: AxiosResponse = await apiClient.get(url, config);

  // ✅ LÓGICA MEJORADA:
  // Si la respuesta directa (response.data) es una lista, la devuelve.
  if (Array.isArray(response.data)) {
    return response.data as T;
  }
  
  // Si es un objeto y contiene la propiedad 'data', devuelve el contenido de 'data'.
  // Esto manejará respuestas como { status: 1, data: [...] }
  if (response.data && typeof response.data === 'object' && 'data' in response.data) {
    return response.data.data as T;
  }

  // Si no es ninguno de los casos anteriores, devuelve la data tal cual.
  return response.data as T;
};
export const getRaw = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  const response: AxiosResponse<T> = await apiClient.get(url, config);
  return response.data; // Devuelve { data: [...], pagination: {...}, ... }
};

/**
 * Realiza una petición POST para crear un nuevo recurso.
 */
const post = async <TResponse, TBody>(url:string, body: TBody, config?: AxiosRequestConfig): Promise<TResponse> => {
  const response: AxiosResponse<ApiResponse<TResponse>> = await apiClient.post(url, body, config);
  return response.data.data;
};

/**
 * Realiza una petición PUT para reemplazar completamente un recurso existente.
 */
const put = async <TResponse, TBody>(url: string, body: TBody, config?: AxiosRequestConfig): Promise<TResponse> => {
  const response: AxiosResponse<ApiResponse<TResponse>> = await apiClient.put(url, body, config);
  return response.data.data;
};

/**
 * Realiza una petición PATCH para actualizar parcialmente un recurso existente.
 */
const patch = async <TResponse, TBody>(url: string, body: TBody, config?: AxiosRequestConfig): Promise<TResponse> => {
  const response: AxiosResponse<ApiResponse<TResponse>> = await apiClient.patch(url, body, config);
  return response.data.data;
};

/**
 * Realiza una petición DELETE para Descartar un recurso.
 * Se renombra a 'remove' para evitar conflictos con la palabra reservada 'delete'.
 */
const remove = async <TResponse>(url: string, config?: AxiosRequestConfig): Promise<TResponse> => {
  const response: AxiosResponse<ApiResponse<TResponse>> = await apiClient.delete(url, config);
  return response.data.data;
};

// Se exporta un objeto 'apiService' que contiene todos los métodos.
// Esto permite hacer llamadas como: apiService.get('/usuarios')
export const apiService = {
  get,
  post,
  put,
  patch,
  delete: remove,
  getRaw,
  getSecondary
};
